/**
 * What a person is told when part of the app has stopped working.
 *
 * EVERY SENTENCE SHE READS IS BUILT HERE, and that rule is the point of the
 * file rather than a tidiness preference. An error boundary only does anything
 * when React renders and something throws, and this repo has no browser test
 * harness — so any wording left inside the component is wording nothing can
 * check. That has already gone wrong twice: once when the component built its
 * own headline and string-replaced the screen name afterwards (every assertion
 * passed and the screen said "This screen"), and once when the escalation
 * message kept its own pluralisation and got it wrong.
 *
 * WHY THERE IS NO "your page is out of date" CASE.
 *
 * There was one, and it was dead code. The app is a single bundle — nothing in
 * `src/` uses `import()` or `React.lazy` — so a stale deploy fails at the
 * `<script>` tag before React exists and no boundary is ever mounted to catch
 * it. `index.html` handles that case, and knows something this file did not:
 * Cloudflare serves the SPA fallback for a missing asset with a four-hour
 * cache, so a plain reload cannot escape it.
 */

export type Crash = {
  /** The sentence. Never a stack trace. */
  headline: string
  /** What she can do about it, from where she is. */
  advice: string
  /** The developer's version, for Chris. Empty when there is nothing useful. */
  technical: string
}

/**
 * A message long enough to be a payload rather than a sentence is truncated.
 * PostgREST puts whole rows in an error, and printing that under "For Chris" on
 * the screen of the person this copy protects from technical detail is the
 * opposite of the intent.
 */
export const MAX_TECHNICAL = 300

/**
 * Something useful about what was thrown, for the one line aimed at Chris.
 *
 * The awkward cases are all real. `String({})` is `"[object Object]"` — noise
 * pretending to be a clue. But `JSON.stringify(new Map())` is `"{}"`, which is
 * worse: it is noise pretending to be an empty object, and it hides the one
 * fact worth having, which is what kind of thing it was. So the constructor
 * name leads, and the JSON is only added when it says something.
 */
function technicalOf(error: unknown): string {
  if (error === null || error === undefined) return ''
  if (error instanceof Error) return clip(`${error.name}: ${error.message}`)
  if (typeof error !== 'object') return clip(String(error))

  const name = error.constructor?.name ?? 'Object'
  const body = safeJson(error)
  const article = /^[AEIOU]/i.test(name) ? 'an' : 'a'
  return clip(body && body !== '{}' ? `${name} ${body}` : `${article} ${name} was thrown`)
}

const clip = (text: string) =>
  text.length > MAX_TECHNICAL ? `${text.slice(0, MAX_TECHNICAL)}…` : text

function safeJson(value: object): string {
  try {
    return JSON.stringify(value) ?? ''
  } catch {
    // Circular, or a getter that throws. Both are things that happen.
    return ''
  }
}

/**
 * Where the crash card is standing, which changes what she can be told to do.
 *
 * `inside` — the header, search box and tabs are still on screen above it.
 * `alone`  — the whole app came down, so there is nothing else on the page.
 *            Telling her to "use the tabs at the top" there is a sentence about
 *            furniture that is not in the room.
 */
export type CrashScope = 'inside' | 'alone'

export function describeCrash(error: unknown, where: string, scope: CrashScope = 'inside'): Crash {
  /**
   * WHAT THIS DELIBERATELY DOES NOT SAY.
   *
   * It used to say "nothing was changed", which is a claim about the database
   * that a render crash cannot make. Two real paths break it: a throw while
   * Bulk redraws its progress card with four hundred emails already gone and
   * the send loop still running, and a throw after `approve()` in Drips has
   * written forty drafts.
   *
   * It also does not lead with "reload". Reloading is what the Bulk screen's
   * own banner tells her not to do while a send is in flight, and nothing here
   * can know whether one is.
   */
  const common =
    'It stopped rather than show you something half-finished. Anything already saved or sent is unaffected — this is the screen, not your work.'

  const next =
    scope === 'inside'
      ? 'Try it again below, or use the tabs at the top to go somewhere else.'
      : 'Reloading the page is the only way back from here.'

  return {
    headline: `${where} has stopped.`,
    advice: `${common} ${next} If it keeps happening, tell Chris what you were doing.`,
    technical: technicalOf(error),
  }
}

/**
 * The line that appears once "try again" has been tried and has not worked.
 *
 * `attempts` counts presses of the retry button **within this one failure**. A
 * retry that succeeded and a fresh crash twenty minutes later is not two
 * failures of the same thing, and saying "this is not going to recover on its
 * own" about something that already did is both false and an instruction to
 * stop trying.
 *
 * Returns null when there is nothing to add — the first failure says nothing
 * extra, because telling her up front that the button might not work would
 * undercut the button.
 */
export function escalation(attempts: number): string | null {
  if (attempts < 1) return null
  const times = attempts === 1 ? 'twice' : `${attempts + 1} times`
  return `That has now failed ${times}. This screen is not going to fix itself — go to another tab and tell Chris what you were doing.`
}

/**
 * The warning that sits beside the reload button.
 *
 * Which hazard matters depends on what crashed, and getting it wrong is worse
 * than saying nothing: the update line's fallback used to warn about email
 * sends while having just thrown away an in-flight roster pull.
 */
export const RELOAD_WARNING = {
  screen: 'If an email send is running, do not reload — let it finish first.',
  strip: 'If an update was running, it has been lost. Press Update again once the page is back.',
} as const
